import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of, BehaviorSubject } from "rxjs";
import { ApolloGraphQlService } from "../apollo/apollo-graphql.service";
import * as _ from "lodash";
import { map } from "rxjs/operators";

import { TableBuilder } from "src/app/models/graphQL/table-graphQL_request";
import { TableResponseGraphQL } from "src/app/models/graphQL/table-graphQL.response";
import { TipoDatoTableFieldGQlBuilder } from "src/app/models/graphQL/tipo-dato-table-field-GQL.request";
import { TipoDatoTableFieldGQLResponse } from "src/app/models/graphQL/tipo-dato-table-field-GQL.response";
import { AddTipoDatoTableDataBuilder } from "src/app/models/graphQL/mutations/tables/addTipoDatoTableData.mutation";
import { DeleteTipoDatoTableDataBuilder } from "src/app/models/graphQL/mutations/tables/deleteTipoDatoTableData.mutation";

import { Table } from "src/app/models/table/table.model";
import { TableField } from "src/app/models/table/table.field.model";
import { TipoDatoTableDatasGQlBuilder } from "src/app/models/graphQL/tables/tipo-dato-table-datas-GQL.request";
import {
  ResultType,
  TipoDatoTableDatasGQLResponse,
} from "src/app/models/graphQL/tables/tipo-dato-table-datas-GQL.response";
import {
  GridDataType,
  TableFieldData,
  GridData,
} from "src/app/models/table/table.gtid.model";
import { EditTipoDatoTableDataBuilder } from "src/app/models/graphQL/mutations/tables/editTipoDatoTableData.mutation";
import { MutationOptions, QueryOptions } from "@apollo/client";

@Injectable({
  providedIn: "root",
})
export class TableService {
  constructor(
    private apolloService: ApolloGraphQlService,
    private http: HttpClient
  ) {}

  getAllTables(): Observable<Array<Table>> {
    const queryOptions: QueryOptions = new TableBuilder().build();
    const allTablesCollection =
      this.apolloService.executeQueryWithMappedFunction(
        queryOptions,
        this.toTableResponse
      );
    if (!_.isEmpty(allTablesCollection)) {
      return allTablesCollection;
    }
  }

  getTableFields(tableId: string): Observable<Array<TableField>> {
    const queryOptions: QueryOptions = new TipoDatoTableFieldGQlBuilder()
      .withTableId(tableId)
      .build();
    const allTableFieldCollection =
      this.apolloService.executeQueryWithMappedFunctionAndReferenceThis(
        queryOptions,
        this.toTableFieldsResponse,
        this
      );
    if (!_.isEmpty(allTableFieldCollection)) {
      return allTableFieldCollection;
    }
  }

  getTableAllData(
    tableId: string,
    first: number,
    offset: number,
    structData: TableField[]
  ): Observable<Array<GridDataType>> {
    const queryOptions: QueryOptions = new TipoDatoTableDatasGQlBuilder()
      .withTableId(tableId)
      .withFirst(first)
      .withOffset(offset)
      .withStructData(structData)
      .build();
    const otherData = { paramStructData: structData, referenceThis: this };

    const allDataCollection =
      this.apolloService.executeQueryWithMappedFunctionAndOtherData(
        queryOptions,
        this.toTableAllDataResponse,
        otherData
      );

    if (!_.isEmpty(allDataCollection)) {
      return allDataCollection;
    }
  }

  addTipoDatoTableData(uuidTable: string, input: string): Observable<any> {
    const mutationOptions: MutationOptions = new AddTipoDatoTableDataBuilder()
      .withUuidTable(uuidTable)
      .withInput(input)
      .build();

    return this.apolloService.executeMutation(mutationOptions);
  }

  editTipoDatoTableData(
    uuidTable: string,
    recordId: string,
    input: string
  ): Observable<any> {
    const mutationOptions: MutationOptions = new EditTipoDatoTableDataBuilder()
      .withUuidTable(uuidTable)
      .withInput(input)
      .withRecordId(recordId)
      .build();

    return this.apolloService.executeMutation(mutationOptions);
  }

  deleteTipoDatoTableData(
    uuidTable: string,
    regDeleteId: string
  ): Observable<any> {
    const mutationOptions: MutationOptions =
      new DeleteTipoDatoTableDataBuilder()
        .withUuidTable(uuidTable)
        .withRegDeleteId(regDeleteId)
        .build();

    return this.apolloService.executeMutation(mutationOptions);
  }

  private toTableResponse(
    tableFromGraphQLCollection: TableResponseGraphQL
  ): Array<Table> {
    const newTableCollection: Table[] = [];
    for (const tableResponse of tableFromGraphQLCollection.findTipoDatoTables) {
      const newTable = new Table();
      newTable.uuid = tableResponse.uuid;
      newTable.nombre_es = tableResponse.nombre_es;
      newTable.nombre_en = tableResponse.nombre_en;

      newTableCollection.push(newTable);
    }
    return newTableCollection;
  }

  private toTableFieldsResponse(
    tableFieldsFromGraphQLCollection: TipoDatoTableFieldGQLResponse,
    referenceServices: any
  ): Array<TableField> {
    let newTableFieldCollection: TableField[] = [];

    newTableFieldCollection = referenceServices.toTableFieldsResponseRecursive(
      tableFieldsFromGraphQLCollection.findTipoDatoTableFields,
      referenceServices
    );

    return newTableFieldCollection;
  }

  public toTableFieldsResponseRecursive(
    tableFieldsFromGraphQLCollection: any,
    referenceServices: any
  ): Array<TableField> {
    const newTableFieldCollection: TableField[] = [];

    for (const tableFieldResponse of tableFieldsFromGraphQLCollection) {
      const newTableField = new TableField();

      newTableField.uuid = tableFieldResponse.uuid;
      newTableField.nombre = tableFieldResponse.nombre;
      newTableField.nombre_en = tableFieldResponse.nombre_en;
      newTableField.nombre_es = tableFieldResponse.nombre_es;
      newTableField.codigo = tableFieldResponse.codigo;
      newTableField.tipo = tableFieldResponse.tipo;
      newTableField.isUnique = tableFieldResponse.isUnique;
      newTableField.isEditable = tableFieldResponse.isEditable;
      newTableField.hidden = tableFieldResponse.hidden;
      newTableField.codigo_df = tableFieldResponse.codigo_df;
      newTableField.isRequired = tableFieldResponse.isRequired;

      if (tableFieldResponse.tipo.toUpperCase().trim() == "OBJECT") {
        newTableField.childField =
          referenceServices.toTableFieldsResponseRecursive(
            tableFieldResponse.definitions,
            referenceServices
          );
      }

      newTableFieldCollection.push(newTableField);
    }

    return newTableFieldCollection;
  }

  private toTableAllDataResponse(
    tipoDatoTableDatasGraphQLCollection: TipoDatoTableDatasGQLResponse,
    otherData: any
  ): Array<GridDataType> {
    let allDataCollection: Array<GridDataType> = [];
    let newRowData: GridDataType;
    let newFieldData: TableFieldData;
    let resultAllData: ResultType[] =
      tipoDatoTableDatasGraphQLCollection.findTipoDatoTableDatas.results;

    for (const resultData of resultAllData) {
      newRowData = <GridDataType>{};
      newRowData.id = resultData._id;

      for (const tableField of otherData.paramStructData) {
        newFieldData = <TableFieldData>{};

        newFieldData.type = tableField.tipo;
        newRowData[tableField.uuid] = newFieldData;

        if (tableField.tipo == "Object") {
          newFieldData.value = tableField.codigo_df;
          if (resultData[tableField.codigo_df]) {
            newFieldData.childRow =
              otherData.referenceThis.toTableAllDataRecursive(
                resultData[tableField.codigo_df],
                tableField.childField,
                otherData.referenceThis
              );
          }
        } else {
          newFieldData.value =
            resultData[tableField.codigo_df] != null
              ? resultData[tableField.codigo_df].value
              : null;
        }
      }

      allDataCollection.push(newRowData);
    }

    return allDataCollection;
  }

  public toTableAllDataRecursive(
    resultData: any,
    structData: TableField[],
    referenceThis: any
  ): Array<GridDataType> {
    let allDataCollection: Array<GridDataType> = [];
    let newRowData: GridDataType;
    let newFieldData: TableFieldData;

    for (const tableField of structData) {
      newFieldData = <TableFieldData>{};
      newRowData = <GridDataType>{};
      newFieldData.type = tableField.tipo;

      if (tableField.tipo == "Object") {
        newFieldData.value = tableField.codigo_df;
        if (resultData[tableField.codigo_df]) {
          newFieldData.childRow = referenceThis.toTableAllDataRecursive(
            resultData[tableField.codigo_df],
            tableField.childField,
            referenceThis
          );
        }
      } else {
        newFieldData.value =
          resultData[tableField.codigo_df] != null
            ? resultData[tableField.codigo_df].value
            : null;
      }

      newRowData[tableField.uuid] = newFieldData;
      allDataCollection.push(newRowData);
    }

    return allDataCollection;
  }
}
